import React from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";

import { IssuesBoard } from "#app/components/issues/issues-board";
import { IssuesTable } from "#app/components/issues/issues-table";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { useWorkflowId } from "#app/lib/hooks/use-workflow-id";
import { useViews } from "#app/lib/views/view-context";
import { Dashboard } from "#app/widgets/dashboards/dashboard";
import { useRecordDashboardVisit } from "#app/components/dashboards/use-recent-dashboards";
import { routePath } from "#app/lib/hooks/use-app-navigate";

/**
 * `/workflow/$workflowId/view/$viewId` — every saved view a workflow has
 * beyond its built-ins.
 *
 * ONE route for all of them: the body is chosen by the view's `type`, not by
 * the URL, so adding a view adds a row and never a route file. The built-ins
 * keep their own segments (`/list`, `/board`, `/dashboard`) because those
 * paths are in bookmarks and in `preferences.defaultRoute`.
 *
 * `key={activeView.id}` is load-bearing: the bodies seed mount-only state
 * from the view (the local display config, the Only-me applied value), and
 * two saved views on this one route would otherwise hand the second view the
 * first one's state.
 */
function WorkflowViewRoute() {
  const { workspace } = Route.useParams();
  const workflowId = useWorkflowId();
  const { views, activeView, initialLoading, loadError, retry } = useViews();
  const scope = React.useMemo(
    () => ({ kind: "workflow" as const, workflowId: workflowId ?? "" }),
    [workflowId],
  );
  useRecordDashboardVisit("workflow", workflowId, activeView);

  if (!workflowId) return null;

  // The tab row arrives with its query, so a direct link lands here before the
  // view it names exists on the client.
  if (initialLoading) {
    return null;
  }

  const workflowPath = `/${workspace}/workflow/${workflowId}`;

  // A saved view has no built-in body to fall back on, so a failed load shows
  // the failure here rather than landing on a workflow index that would have
  // nothing to resolve either.
  if (views.length === 0 && loadError) {
    return <LoadErrorState error={loadError} noun="views" onRetry={retry} />;
  }

  // A deleted view's URL outlives it — in another tab, in a bookmark. Land on
  // the workflow, which resolves whichever view is the default now (or the
  // `/board` floor, whose body renders on an empty row instead of bouncing).
  if (!activeView) {
    return <Navigate to={routePath(workflowPath)} replace />;
  }

  if (activeView.type === "board") {
    return <IssuesBoard key={activeView.id} scope={scope} />;
  }

  if (activeView.type === "dashboard") {
    return (
      <Dashboard
        key={activeView.id}
        scope={scope}
        // The server derives the view's widget key on the row, so the board
        // this renders and the board the API accepts writes to are one
        // value with one author.
        viewKey={activeView.widgetView ?? undefined}
      />
    );
  }

  // `list`, and the floor for a type a later build persists: the flat issue
  // table renders any workflow's projects.
  return <IssuesTable key={activeView.id} scope={scope} />;
}

export const Route = createFileRoute(
  "/(app)/$workspace/workflow/$workflowId/view/$viewId",
)({
  component: WorkflowViewRoute,
});
